import { getWorkOrderStatusDisplayLabel, WORK_ORDER_STATUS } from 'settings/constants/service'

import type { WorkOrderStatusEventApi } from 'types/api/work-order/workOrderStatus'

const { CANCELED, HOLD, SUSPENDED, VACATION_HOLD, SCHEDULED, SERVICED, SKIPPED, IN_PROCESS, PARTIALLY_SERVICED, PARTIALLY_SKIPPED } =
  WORK_ORDER_STATUS

/**
 * WorkOrderIntentStatus (crm-common) → FE labels; the enum spells it `Cancelled`, the label
 * `Canceled`. Standard, Deleted and Restored are absent on purpose — no chip, no intent.
 */
const INTENT_TO_DISPLAY: Record<string, string> = {
  HOLD,
  VACATION: VACATION_HOLD,
  CANCELLED: CANCELED,
  SUSPENDED,
}

export interface WorkOrderStatusFields {
  /** Legacy work_order.work_order_status. A Mobile 2.0 stop stores its own roll-up here. */
  workOrderStatus?: string | null
  /** WorkOrderCompletionRollupStatus — the per-container events folded by the backend. */
  completionStatus?: string | null
  /** WorkOrderIntentStatus — Hold/Cancelled/Suspended are set apart from completion. */
  intentStatus?: string | null
  /** The same intent under the name the backend is moving to; preferred when both arrive. */
  userIntentStatus?: string | null
}

/** The completion a stop reads as. A per-container write never updates the legacy column, so the roll-up is the fresher of the two. */
export const stopStatus = (stop?: Pick<WorkOrderStatusFields, 'completionStatus' | 'workOrderStatus'> | null): string | null | undefined =>
  stop?.completionStatus || stop?.workOrderStatus

/** The two dimensions a status reads on: intent is empty whenever the WO carries none. */
export interface WorkOrderStatusChips {
  intent: string
  completion: string
}

/**
 * Intent and completion as two labels rather than one. Both come straight off the response —
 * WorkOrderStatusEnricher already folds the per-container events, so the roll-up is only relabelled.
 */
export const resolveIntentLabel = (rawIntent?: string | null): string =>
  rawIntent ? INTENT_TO_DISPLAY[String(rawIntent).toUpperCase()] ?? '' : ''

const INTENT_LABELS = Object.values(INTENT_TO_DISPLAY)

export interface DisplayedStatus {
  /** The label a one-status surface renders. */
  status: string
  /** The completion once a can has been touched; empty while the stop is untouched. */
  completion: string
  /** The intent label, empty when the work order carries none. */
  intent: string
  /** The intent shown beside the completion; empty while the intent is the status itself. */
  sideIntent: string
  /** The completion shown beside a Canceled status; empty unless the driver's result was kept. */
  sideCompletion: string
}

/**
 * The intent alone while no can has been touched (a legacy Hold or Canceled that only mirrors it counts), otherwise the completion
 * with the intent beside it. Canceled always leads: the stop is off the route whatever the driver reported, so the completion rides beside.
 */
export const resolveDisplayedStatus = (rawStatus?: string | null, rawIntent?: string | null): DisplayedStatus => {
  const label = getWorkOrderStatusDisplayLabel(rawStatus, rawStatus ?? '')
  const intent = resolveIntentLabel(rawIntent)
  const isUntouched = !label || label === SCHEDULED || (Boolean(intent) && INTENT_LABELS.includes(label))
  const intentLeads = isUntouched || intent === CANCELED
  return {
    status: intentLeads && intent ? intent : label,
    completion: isUntouched ? '' : label,
    intent,
    sideIntent: intentLeads ? '' : intent,
    sideCompletion: !isUntouched && intent === CANCELED ? label : '',
  }
}

export const resolveWorkOrderStatusChips = ({
  workOrderStatus,
  completionStatus,
  intentStatus,
  userIntentStatus,
}: WorkOrderStatusFields): WorkOrderStatusChips => ({
  intent: resolveIntentLabel(userIntentStatus ?? intentStatus),
  completion: getWorkOrderStatusDisplayLabel(completionStatus) || getWorkOrderStatusDisplayLabel(workOrderStatus, workOrderStatus ?? ''),
})

/**
 * What the backend's fold would make of these per-container statuses. Only for rows a user is
 * editing and has not saved: a stored roll-up is read off `completionStatus`, never recomputed.
 */
export const previewRollupLabel = (containerStatuses: Array<string | null | undefined>): string => {
  const labels = Array.from(new Set(containerStatuses.map(status => getWorkOrderStatusDisplayLabel(status)).filter(Boolean)))
  if (labels.length === 1) return labels[0]
  if (labels.includes(SERVICED)) return PARTIALLY_SERVICED
  if (labels.includes(SKIPPED)) return PARTIALLY_SKIPPED
  return IN_PROCESS
}

/**
 * The reason each can was skipped for, read off its latest completion event, deduplicated.
 * Events arrive latest-first and a can's earlier skips are history, so only its newest completion counts.
 */
export const latestSkipReasons = (events?: ReadonlyArray<WorkOrderStatusEventApi> | null): string[] => {
  const seenContainers = new Set<string | null>()
  const reasons = new Set<string>()
  ;(events ?? []).forEach(event => {
    const containerKey = event.containerId ?? null
    if (!event.completionStatus || seenContainers.has(containerKey)) return
    seenContainers.add(containerKey)
    const reason = [event.skipReason?.exceptionType, event.skipReason?.noteText].filter(Boolean).join(' · ')
    if (reason) reasons.add(reason)
  })
  return Array.from(reasons)
}
